import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { abbreviateNumber } from '../../utils/getCurrency';
import { KpiCard, ChannelLegend } from './components';
import { getChannelColor } from './theme/tokens';

/**
 * Headline KPI strip for the Overview screen.
 *
 * Each headline metric shows a big tabular number, a period-over-period trend
 * badge (from analyseit v2 summary.metrics.order_comparison), and an
 * expandable per-channel breakdown (coloured dot + label + value + share bar).
 * Channel colours come from the canonical getChannelColor map, so a given
 * aggregator is the same colour here, in the legend, and in every chart.
 */

const sumBy = (rows, key) =>
  rows.reduce((acc, row) => acc + (+row[key] || 0), 0);

/** order_comparison returns { value: fraction, status: GAIN|LOSE|NEUTRAL }. */
const trendFor = (comparison, key) => {
  const t = comparison && comparison[key];
  if (!t || typeof t.value !== 'number') return undefined;
  return { value: t.value, status: t.status };
};

const KpiStrip = ({
  summary,
  providers,
  breakdownOpen,
  onBreakdownToggle,
  selectedChannelIds,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const controlled = typeof breakdownOpen === 'boolean';
  const open = controlled ? breakdownOpen : internalOpen;
  const toggle = () =>
    controlled
      ? onBreakdownToggle && onBreakdownToggle(!open)
      : setInternalOpen(o => !o);

  const allRows = (summary && summary.summary) || [];
  // A channel multi-select can focus the whole view (aggregate totals,
  // breakdown rows, legend) on a subset of channels; null = all.
  const rows = Array.isArray(selectedChannelIds)
    ? allRows.filter(r => selectedChannelIds.includes(r.provider_id))
    : allRows;
  const comparison =
    (summary && summary.metrics && summary.metrics.order_comparison) || {};

  const currencyObj = {
    code: rows[0] && rows[0].currency,
    symbol: (rows[0] && rows[0].currency_symbol) || '$',
  };
  const money = cents =>
    `${currencyObj.symbol}${abbreviateNumber((+cents || 0) / 100)}`;

  const channelName = providerId => {
    const p = (providers || []).find(x => x.id === providerId);
    return (p && p.title) || `Channel ${providerId}`;
  };

  const grossOf = r => +r.gross_revenue || +r.realized_revenue || 0;
  const rateOf = r =>
    +r.total_orders > 0 ? (+r.cancelled_orders / +r.total_orders) * 100 : 0;

  const showBreakdown = allRows.length > 1;

  // Build an expandable per-channel breakdown for a metric.
  // mode 'sum' → share is value / total; mode 'rate' → share is value / 100.
  const buildBreakdown = (rawFn, fmt, mode = 'sum') => {
    if (!showBreakdown) return undefined;
    const items = rows.map((r, i) => {
      const raw = rawFn(r) || 0;
      return {
        id: r.provider_id != null ? r.provider_id : i,
        label: channelName(r.provider_id),
        color: getChannelColor(r.provider_id, i),
        raw,
        value: fmt(raw),
      };
    });
    const total = items.reduce((a, b) => a + b.raw, 0);
    const shareOf = raw => {
      if (mode === 'rate') return raw / 100;
      return total > 0 ? raw / total : 0;
    };
    return items
      .map(it => ({ ...it, share: shareOf(it.raw) }))
      .sort((a, b) => b.raw - a.raw);
  };

  const totalOrders = sumBy(rows, 'total_orders');
  const completedOrders = sumBy(rows, 'completed_orders');
  const cancelledOrders = sumBy(rows, 'cancelled_orders');
  const grossRevenue = rows.reduce((acc, r) => acc + grossOf(r), 0);
  const netRevenue = sumBy(rows, 'net_revenue');
  const cancellationRate =
    totalOrders > 0 ? (cancelledOrders / totalOrders) * 100 : 0;

  const num = n => (+n || 0).toLocaleString();
  const pct = n => `${(+n || 0).toFixed(1)}%`;

  const cards = [
    {
      metricKey: 'totalOrders',
      value: num(totalOrders),
      trend: trendFor(comparison, 'total_orders'),
      positiveIsGood: true,
      breakdown: buildBreakdown(r => +r.total_orders, num),
    },
    {
      metricKey: 'completedOrders',
      value: num(completedOrders),
      trend: trendFor(comparison, 'completed_orders'),
      positiveIsGood: true,
      breakdown: buildBreakdown(r => +r.completed_orders, num),
    },
    {
      metricKey: 'cancelledOrders',
      value: num(cancelledOrders),
      trend: trendFor(comparison, 'cancelled_orders'),
      positiveIsGood: false,
      breakdown: buildBreakdown(r => +r.cancelled_orders, num),
    },
    {
      metricKey: 'grossRevenue',
      value: money(grossRevenue),
      trend: trendFor(comparison, 'gross_revenue'),
      positiveIsGood: true,
      breakdown: buildBreakdown(grossOf, money),
    },
    {
      metricKey: 'netRevenue',
      value: money(netRevenue),
      trend: trendFor(comparison, 'net_revenue'),
      positiveIsGood: true,
      breakdown: buildBreakdown(r => +r.net_revenue, money),
    },
    {
      metricKey: 'cancellationRate',
      value: pct(cancellationRate),
      trend: undefined, // no dedicated comparison field for the derived rate
      positiveIsGood: false,
      breakdown: buildBreakdown(rateOf, pct, 'rate'),
    },
  ];

  const legendItems = rows.map((r, i) => ({
    id: r.provider_id != null ? r.provider_id : i,
    label: channelName(r.provider_id),
    color: getChannelColor(r.provider_id, i),
  }));

  return (
    <div className="flex flex-col gap-3">
      {showBreakdown && !controlled && (
        <div className="flex justify-end">
          <button
            type="button"
            onClick={toggle}
            aria-expanded={open}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-grey-darker hover:bg-grey-light hover:text-primary"
          >
            Channel Summary
            <i
              className="sc-keyboard-arrow-down"
              style={{
                transition: 'transform 0.15s ease',
                transform: open ? 'rotate(180deg)' : 'none',
              }}
            />
          </button>
        </div>
      )}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
        {cards.map(card => (
          <KpiCard
            key={card.metricKey}
            metricKey={card.metricKey}
            value={card.value}
            trend={card.trend}
            positiveIsGood={card.positiveIsGood}
            breakdown={card.breakdown}
            open={open}
          />
        ))}
      </div>
      {showBreakdown && open && <ChannelLegend items={legendItems} />}
    </div>
  );
};

KpiStrip.propTypes = {
  summary: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
  providers: PropTypes.array,
  breakdownOpen: PropTypes.bool,
  onBreakdownToggle: PropTypes.func,
  selectedChannelIds: PropTypes.arrayOf(PropTypes.number),
};

export default KpiStrip;
